import React from 'react';
import { Panel } from 'rsuite';
import { PPCMetrics } from '@/types/primaryLedgerSnapshots';
import MetricCard from './MetricCard';

interface PPCMetricsSectionProps {
  metrics: PPCMetrics;
}

const formatCurrency = (value: number): string => {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const formatPercentage = (value: number): string => {
  return (value * 100).toFixed(1);
};

const formatInteger = (value: number): string => {
  return Math.round(value).toLocaleString('en-US');
};

const formatDecimal = (value: number, decimals: number = 2): string => {
  return value.toFixed(decimals);
};

const PPCMetricsSection: React.FC<PPCMetricsSectionProps> = ({ metrics }) => {
  return (
    <Panel 
      header={<span className="text-lg font-semibold text-gray-900 dark:text-gray-100">PPC Results</span>}
      bordered
      className="mb-6"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <MetricCard
            label="Impressions"
            value={metrics.impressions.cumulative}
            formatValue={formatInteger}
          />
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-2">
            Avg: {formatInteger(metrics.impressions.dailyAverage.current)}/day
          </div>
        </div>

        <div>
          <MetricCard
            label="Clicks"
            value={metrics.clicks.cumulative}
            formatValue={formatInteger}
          />
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-2">
            Avg: {formatInteger(metrics.clicks.dailyAverage.current)}/day
          </div>
        </div>

        <div>
          <MetricCard
            label="Spend"
            value={metrics.spend.cumulative}
            formatValue={formatCurrency}
            prefix="$"
          />
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-2">
            Avg: ${formatCurrency(metrics.spend.dailyAverage.current)}/day
          </div>
        </div>

        <div>
          <MetricCard
            label="Sales"
            value={metrics.sales.cumulative}
            formatValue={formatCurrency}
            prefix="$"
          />
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-2">
            Avg: ${formatCurrency(metrics.sales.dailyAverage.current)}/day
          </div>
        </div>

        <div>
          <MetricCard
            label="Orders"
            value={metrics.orders.cumulative}
            formatValue={formatInteger}
          />
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-2">
            Avg: {formatInteger(metrics.orders.dailyAverage.current)}/day
          </div>
        </div>

        <div>
          <MetricCard
            label="Units"
            value={metrics.units.cumulative}
            formatValue={formatInteger}
          />
          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-2">
            Avg: {formatInteger(metrics.units.dailyAverage.current)}/day
          </div>
        </div>

        <MetricCard
          label="CTR"
          value={metrics.ctr}
          formatValue={formatPercentage}
          suffix="%"
        />

        <MetricCard
          label="CPC"
          value={metrics.cpc}
          formatValue={formatCurrency}
          prefix="$"
        />

        <MetricCard
          label="ACoS"
          value={metrics.acos}
          formatValue={formatPercentage}
          suffix="%"
        />

        <MetricCard
          label="ROAS"
          value={metrics.roas}
          formatValue={(val) => formatDecimal(val, 2)}
        />

        <MetricCard
          label="Conversion Rate"
          value={metrics.conversionRate}
          formatValue={formatPercentage}
          suffix="%"
        />
      </div>
    </Panel>
  );
};

export default PPCMetricsSection;